if (!customElements.get("modal-dialog")) {
  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]').addEventListener(
        "click",
        this.hide.bind(this, false),
      );
      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      if (this.classList.contains("media-modal")) {
        this.addEventListener("pointerup", (event) => {
          if (
            event.pointerType === "mouse" &&
            !event.target.closest("deferred-media, product-model")
          )
            this.hide();
        });
      } else {
        this.addEventListener("click", (event) => {
          if (event.target === this) this.hide();
        });
      }
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      this.openedBy = opener;
      const popup = this.querySelector(".template-popup");
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      if (popup) popup.loadContent();
      trapFocus(this, this.querySelector('[role="dialog"]'));
      window.pauseAllMedia();
    }

    hide() {
      document.body.classList.remove("overflow-hidden");
      document.body.dispatchEvent(new CustomEvent("modalClosed"));
      this.removeAttribute("open");
      window.pauseAllMedia();
    }
  }

  customElements.define("modal-dialog", ModalDialog);
}

window.ModalOpener =
  window.ModalOpener ||
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector("button");

      if (!button) return;
      button.addEventListener("click", () => {
        const modal = document.querySelector(this.getAttribute("data-modal"));
        if (modal) modal.show(button);
      });
    }
  };

if (!customElements.get("modal-opener"))
  customElements.define("modal-opener", window.ModalOpener);

window.DeferredMedia =
  window.DeferredMedia ||
  class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    loadContent(focus = true) {
      window.pauseAllMedia();
      if (!this.getAttribute("loaded")) {
        const content = document.createElement("div");
        content.appendChild(
          this.querySelector("template").content.firstElementChild.cloneNode(
            true,
          ),
        );

        this.setAttribute("loaded", true);
        const deferredElement = this.appendChild(
          content.querySelector("video, model-viewer, iframe"),
        );
        if (focus) deferredElement.focus();
        if (
          deferredElement.nodeName == "VIDEO" &&
          deferredElement.getAttribute("autoplay")
        ) {
          deferredElement.play();
        }
      }
    }
  };

if (!customElements.get("deferred-media"))
  customElements.define("deferred-media", window.DeferredMedia);

if (!customElements.get("slider-component")) {
  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('[id^="Slider-"]');
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.enableSliderLooping = false;
      this.currentPageElement = this.querySelector(".slider-counter--current");
      this.pageTotalElement = this.querySelector(".slider-counter--total");
      this.prevButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.handleSliderScroll = this.handleSliderScroll.bind(this);
      this.handleButtonClick = this.onButtonClick.bind(this);
      this.handleResize = this.initPages.bind(this);
      this.scrollRaf = null;
      this.sortImages();

      if (!this.slider || !this.nextButton) return;

      this.initPages();
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.slider);

      this.slider.addEventListener("scroll", this.handleSliderScroll, {
        passive: true,
      });
      this.prevButton.addEventListener("click", this.handleButtonClick);
      this.nextButton.addEventListener("click", this.handleButtonClick);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.slider) {
        this.slider.removeEventListener("scroll", this.handleSliderScroll);
      }
      this.prevButton?.removeEventListener("click", this.handleButtonClick);
      this.nextButton?.removeEventListener("click", this.handleButtonClick);
      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = null;
      }
    }

    handleSliderScroll() {
      if (this.scrollRaf) return;

      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.update();
      });
    }

    sortImages() {
      if (!this.closest("media-gallery")) {
        return;
      }
      const slides = Array.from(this.sliderItems);

      const firstSlide = slides.shift();

      slides.sort((slideA, slideB) => {
        const aData = slideA.getAttribute("data-variant-grouping-ids")?.trim();
        const bData = slideB.getAttribute("data-variant-grouping-ids")?.trim();

        const aHasVariant = aData && aData !== "";
        const bHasVariant = bData && bData !== "";

        if (aHasVariant && !bHasVariant) {
          return -1;
        }

        if (!aHasVariant && bHasVariant) {
          return 1;
        }

        return 0;
      });

      // We don't touch firstSlide, it's already the first child and moving it resets animations.
      const fragment = document.createDocumentFragment();
      slides.forEach((slide) => fragment.appendChild(slide));
      this.slider.appendChild(fragment);
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    }

    initPages() {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter(
        (element) => element.clientWidth > 0,
      );
      if (this.sliderItemsToShow.length < 2) return;
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset,
      );
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    }

    resetPages() {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
      this.initPages();
    }

    update() {
      if (!this.slider || !this.nextButton) return;

      // --- 1. DOM Read Phase ---
      this.currentPage =
        Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

      const isFirstVisible = this.isSlideVisible(this.sliderItemsToShow[0]);
      const isLastVisible = this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      );
      const isScrollZero = this.slider.scrollLeft === 0;

      // --- 2. DOM Write Phase ---
      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.enableSliderLooping) return;

      if (isFirstVisible && isScrollZero) {
        this.prevButton.setAttribute("disabled", "disabled");
      } else {
        this.prevButton.removeAttribute("disabled");
      }

      if (isLastVisible) {
        this.nextButton.setAttribute("disabled", "disabled");
      } else {
        this.nextButton.removeAttribute("disabled");
      }
    }

    isSlideVisible(element, offset = 0) {
      if (!element) {
        console.error("element not found");
        return;
      }
      const lastVisibleSlide =
        this.slider.clientWidth + this.slider.scrollLeft - offset;
      return (
        element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
        element.offsetLeft >= this.slider.scrollLeft
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      this.style.pointerEvents = "none";
      const step = event.currentTarget.dataset.step || 1;
      this.slideScrollPosition =
        event.currentTarget.name === "next"
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
      this.style.pointerEvents = "all";
    }

    setSlidePosition(position) {
      this.slider.scrollTo({
        left: position,
      });
    }
  }

  customElements.define("slider-component", SliderComponent);

  class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.sliderControlWrapper = this.querySelector(".slider-buttons");
      this.enableSliderLooping = true;

      if (!this.sliderControlWrapper) return;

      this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
      this.handleLinkClick = this.linkToSlide.bind(this);
      this.handleSlideVisibilityScroll = this.setSlideVisibility.bind(this);
      this.handleReducedMotionChange = () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      };
      this.handleFocusIn = this.focusInHandling.bind(this);
      this.handleFocusOut = this.focusOutHandling.bind(this);
      this.handleAutoPlayToggle = this.autoPlayToggle.bind(this);

      this.sliderControlLinksArray = Array.from(
        this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
      );
      this.sliderControlLinksArray.forEach((link) =>
        link.addEventListener("click", this.handleLinkClick),
      );
      this.slider.addEventListener("scroll", this.handleSlideVisibilityScroll, {
        passive: true,
      });
      this.setSlideVisibility();



      if (this.slider.getAttribute("data-autoplay") === "true")
        this.setAutoPlay();
    }

    setAutoPlay() {
      this.autoplaySpeed = this.slider.dataset.speed * 1000;
      if (!this.autoPlayEventsBound) {
        this.addEventListener("mouseover", this.handleFocusIn);
        this.addEventListener("mouseleave", this.handleFocusOut);
        this.addEventListener("focusin", this.handleFocusIn);
        this.addEventListener("focusout", this.handleFocusOut);
        this.autoPlayEventsBound = true;
      }

      if (this.querySelector(".slideshow__autoplay")) {
        if (!this.sliderAutoplayButton) {
          this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
        }
        if (!this.autoPlayButtonBound) {
          this.sliderAutoplayButton.addEventListener(
            "click",
            this.handleAutoPlayToggle,
          );
          this.autoPlayButtonBound = true;
        }
        this.autoplayButtonIsSetToPlay = true;
        this.play();
      } else {
        this.reducedMotion.matches
          ? this.pause()
          : this.play();
      }
    }

    onButtonClick(event) {
      super.onButtonClick(event);
      this.wasClicked = true;

      const isFirstSlide = this.currentPage === 1;
      const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

      if (!isFirstSlide && !isLastSlide) {
        return;
      }

      if (isFirstSlide && event.currentTarget.name === "previous") {
        this.slideScrollPosition =
          this.slider.scrollLeft +
          this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else if (isLastSlide && event.currentTarget.name === "next") {
        this.slideScrollPosition = 0;
      }

      this.setSlidePosition(this.slideScrollPosition);
    }

    setSlidePosition(position) {
      if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
      this.setPositionTimeout = setTimeout(() => {
        this.slider.scrollTo({
          left: position,
        });
      }, 0);
    }

    update() {
      super.update();
      this.prevButton?.removeAttribute("disabled");

      if (!this.sliderControlLinksArray?.length) return;

      this.sliderControlLinksArray.forEach((link) => {
        link.classList.remove("slider-counter__link--active");
        link.removeAttribute("aria-current");
      });

      if (!isNaN(this.currentPage) && this.currentPage > 0 && this.currentPage <= this.sliderControlLinksArray.length) {
        this.sliderControlLinksArray[this.currentPage - 1]?.classList.add(
          "slider-counter__link--active",
        );
        this.sliderControlLinksArray[this.currentPage - 1]?.setAttribute(
          "aria-current",
          true,
        );
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.sliderControlLinksArray?.forEach((link) => {
        link.removeEventListener("click", this.handleLinkClick);
      });
      this.slider?.removeEventListener(
        "scroll",
        this.handleSlideVisibilityScroll,
      );
      this.reducedMotion?.removeEventListener(
        "change",
        this.handleReducedMotionChange,
      );
      if (this.autoPlayEventsBound) {
        this.removeEventListener("mouseover", this.handleFocusIn);
        this.removeEventListener("mouseleave", this.handleFocusOut);
        this.removeEventListener("focusin", this.handleFocusIn);
        this.removeEventListener("focusout", this.handleFocusOut);
      }
      if (this.autoPlayButtonBound) {
        this.sliderAutoplayButton?.removeEventListener(
          "click",
          this.handleAutoPlayToggle,
        );
      }
      clearInterval(this.autoplay);
    }

    autoPlayToggle() {
      this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
      this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
      this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }

    focusOutHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton ||
          this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
      } else if (!this.reducedMotion.matches) {
        this.play();
      }
    }

    focusInHandling(event) {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton =
          event.target === this.sliderAutoplayButton ||
          this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
          this.play();
        } else if (this.autoplayButtonIsSetToPlay) {
          this.pause();
        }
      }
    }

    play() {
      this.slider.setAttribute("aria-live", "off");
      clearInterval(this.autoplay);
      this.autoplay = setInterval(
        this.autoRotateSlides.bind(this),
        this.autoplaySpeed,
      );
    }

    pause() {
      this.slider.setAttribute("aria-live", "polite");
      clearInterval(this.autoplay);
    }

    togglePlayButtonState(pauseAutoplay) {
      if (pauseAutoplay) {
        this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
        this.sliderAutoplayButton.setAttribute(
          "aria-label",
          window.accessibilityStrings.playSlideshow,
        );
      } else {
        this.sliderAutoplayButton.classList.remove(
          "slideshow__autoplay--paused",
        );
        this.sliderAutoplayButton.setAttribute(
          "aria-label",
          window.accessibilityStrings.pauseSlideshow,
        );
      }
    }

    autoRotateSlides() {
      const slideScrollPosition =
        this.currentPage === this.sliderItems.length
          ? 0
          : this.slider.scrollLeft + this.sliderItemOffset;

      this.setSlidePosition(slideScrollPosition);
    }

    setSlideVisibility() {
      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll("a");
        if (index === this.currentPage - 1) {
          if (linkElements.length)
            linkElements.forEach((button) => {
              button.removeAttribute("tabindex");
            });
          item.setAttribute("aria-hidden", "false");
          item.removeAttribute("tabindex");
        } else {
          if (linkElements.length)
            linkElements.forEach((button) => {
              button.setAttribute("tabindex", "-1");
            });
          item.setAttribute("aria-hidden", "true");
          item.setAttribute("tabindex", "-1");
        }
      });
      this.wasClicked = false;
    }



    linkToSlide(event) {
      event.preventDefault();
      const slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth *
          (this.sliderControlLinksArray.indexOf(event.currentTarget) +
            1 -
            this.currentPage);
      this.slider.scrollTo({
        left: slideScrollPosition,
      });
    }
  }

  customElements.define("slideshow-component", SlideshowComponent);
}
